import { useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { MobileBusinessListItem } from '../MobileBusinessListItem';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { MobileBusinessOverlayProps } from './types';

export function MobileBusinessOverlay({
  selectedBusiness,
  showOverlay,
  businessActivities,
  businessesWithCustomAdventures,
  onClose,
  onOverlayClick
}: MobileBusinessOverlayProps) {
  const { t } = useTranslation('public', { keyPrefix: 'homepage.map.overlay' });
  const isMobile = useMediaQuery('(max-width: 768px)');
  const overlayRef = useRef<HTMLDivElement>(null);

  if (!isMobile || !selectedBusiness || !showOverlay) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        ref={overlayRef}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        onClick={onOverlayClick}
        className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-end justify-center"
      >
        <motion.div
          initial={{ y: '100%', scale: 0.95 }}
          animate={{ y: 0, scale: 1 }}
          exit={{ y: '100%', scale: 0.95 }}
          transition={{ duration: 0.4, type: "spring", damping: 25 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md bg-white rounded-t-2xl shadow-2xl overflow-hidden mx-4 mb-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
        >
          {/* Close handle and button */}
          <div className="flex items-center justify-between p-3 border-b bg-gradient-to-r from-slate-50 to-gray-50">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-8 h-1 bg-gray-300 rounded-full shrink-0" />
              <span className="text-sm font-medium text-gray-600 break-words line-clamp-2">
                {t('selectedExperience')}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="min-w-[44px] min-h-[44px] w-11 h-11 p-0 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 flex items-center justify-center touch-manipulation"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* 📱 IMPROVED: Use MobileBusinessListItem for optimal mobile UX */}
          <div className="p-4">
            <MobileBusinessListItem
              business={selectedBusiness}
              activities={businessActivities[selectedBusiness._id] || []}
              onSelect={undefined} // Disable selection since already selected
              onCustomAdventure={(businessId) => {
                window.open(`/custom-adventures/${businessId}`, '_blank');
              }}
              hasCustomAdventures={
                businessesWithCustomAdventures ? 
                businessesWithCustomAdventures.has(selectedBusiness._id) : 
                false
              }
              className="shadow-none border-0 bg-transparent"
            />
          </div>

          {/* Action buttons */}
          <div className="p-4 border-t bg-gradient-to-r from-slate-50 to-gray-50 flex gap-3">
            <Button
              variant="outline"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="flex-1 min-h-[44px] border-gray-300 text-gray-700 hover:bg-gray-100 touch-manipulation focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
            >
              {t('backToMap')}
            </Button>
            <Button
              onClick={() => {
                window.open(`/business/${selectedBusiness._id}`, '_blank');
              }}
              className="flex-1 min-h-[44px] bg-emerald-600 hover:bg-emerald-700 text-white touch-manipulation focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
            >
              {t('viewDetails')}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
} 